import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { Space, Group, UNGROUPED_GROUP_ID } from '@shure/cloud/shared/models/devices';

const MAX_GROUP_DEPTH = 10;
const MAX_GROUPS = 500;

function storeIntegrityError(message: string): Error {
	return new Error(`[GroupStoreService] index integrity violation: ${message}`);
}

function storeUsageError(message: string): Error {
	return new Error(`[GroupStoreService] usage error: ${message}`);
}

@Injectable({ providedIn: 'root' })
export class GroupStoreService {
	private readonly transloco = inject(TranslocoService);

	// Invalidates signal consumers of the map-backed group limit queries.
	private readonly groupStructureVersion = signal(0);

	/**
	 *  All groups by nodeId
	 */
	protected groupIdToGroup = new Map<string, Group>();

	/**
	 *  All cloud spaces by nodeId
	 */
	protected spaceIdToSpace = new Map<string, Space>();

	/**
	 * Parent group nodeId to child group nodeIds
	 */
	protected groupIdToChildrenIds = new Map<string, Set<string>>();

	/**
	 * Group nodeId to member device nodeIds, including those in cloud spaces
	 */
	protected groupIdToMemberDeviceIds = new Map<string, Set<string>>();

	/**
	 *  Space nodeId to member device nodeIds
	 */
	protected spaceIdToMemberDeviceIds = new Map<string, Set<string>>();

	/**
	 * Device nodeId to member group nodeId
	 */
	protected deviceIdToGroupId = new Map<string, string>();

	/**
	 *  Device nodeId to member Space nodeId
	 */
	protected deviceIdToSpaceId = new Map<string, string>();

	/**
	 *  Group nodeId to ancestor group nodeId path (from root to current key)
	 */
	protected groupIdToIdPath = new Map<string, string[]>();

	//
	// Query methods
	//
	public get groupCount(): number {
		return this.groupIdToGroup.size;
	}

	public get spaceCount(): number {
		return this.spaceIdToSpace.size;
	}

	/**
	 * @param groupNodeId
	 * @returns Name path from root ancestor to this group, e.g. ['Root', 'Parent', 'Current']
	 */
	public getGroupNamePath(groupNodeId: string): string[] {
		if (groupNodeId === UNGROUPED_GROUP_ID) {
			return [this.transloco.translate('cloud.device-management.inventory.not-in-a-group')];
		}

		const path = this.groupIdToIdPath.get(groupNodeId);

		if (!path) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		return path.map((ancestorId) => {
			const group = this.groupIdToGroup.get(ancestorId);

			if (!group) {
				throw storeIntegrityError(
					`groupPathCacheMap references group "${ancestorId}" which does not exist in groupsMap`
				);
			}

			return group.name;
		});
	}

	/**
	 * @param groupNodeId
	 * @returns All descendant device nodeIds for the given groupNodeId, including those in cloud spaces
	 */
	public getDescendantDeviceIds(groupNodeId: string): string[] {
		const descendantDeviceIds: Set<string> = new Set<string>();

		const collectDevices = (currentGroupId: string): void => {
			const memberDeviceIds = this.groupIdToMemberDeviceIds.get(currentGroupId);

			if (memberDeviceIds) {
				for (const deviceId of memberDeviceIds) {
					descendantDeviceIds.add(deviceId);
				}
			}

			const childGroupIds = this.groupIdToChildrenIds.get(currentGroupId) ?? new Set<string>();

			for (const childGroupId of childGroupIds) {
				collectDevices(childGroupId);
			}
		};

		collectDevices(groupNodeId);

		return Array.from(descendantDeviceIds);
	}

	/**
	 * @param groupNodeId
	 * @returns Groups with the same parent as the given groupNodeId, excluding the group itself
	 */
	public getGroupSiblings(groupNodeId: string): Group[] {
		const group = this.getGroup(groupNodeId);

		// root level groups have no siblings
		if (!group.parentId) {
			return this.getTopLevelGroups().filter((sibling) => sibling.id !== groupNodeId);
		}

		return this.getGroupChildren(group.parentId).filter((sibling) => sibling.id !== groupNodeId);
	}

	/**
	 * @param parentGroupId - optional parent group ID. If undefined, returns sibling names of top-level groups.
	 * @returns Names of all sibling groups under the specified parent group (or top-level if undefined).
	 */
	public getSiblingNamesForNewGroup(parentGroupId?: string): string[] {
		const siblings = parentGroupId ? this.getGroupChildren(parentGroupId) : this.getTopLevelGroups();
		return siblings.map((group) => group.name);
	}

	/**
	 * @param groupNodeId - parent group nodeId
	 * @returns Child groups of the given parent
	 */
	public getGroupChildren(groupNodeId: string): Group[] {
		const childIds = Array.from(this.groupIdToChildrenIds.get(groupNodeId) ?? []);

		return childIds.map((childId) => {
			const child = this.groupIdToGroup.get(childId);

			if (!child) {
				throw storeIntegrityError(
					`childGroupsIndexMap references group "${childId}" which does not exist in groupsMap`
				);
			}

			return child;
		});
	}

	/**
	 * @param spaceNodeId
	 * @returns Array containing the IDs of all devices in the space
	 */
	public getSpaceMemberDeviceIds(spaceNodeId: string): string[] {
		return Array.from(this.spaceIdToMemberDeviceIds.get(spaceNodeId) ?? []);
	}

	/**
	 * @param deviceNodeId
	 * @returns Member Space object and all member device nodeIds (inclusive), null if the device is not in a Space
	 */
	public getDeviceSpaceInfo(deviceNodeId: string): { space: Space; deviceIds: string[] } | null {
		const spaceId = this.deviceIdToSpaceId.get(deviceNodeId);

		if (!spaceId) {
			return null;
		}

		const space = this.spaceIdToSpace.get(spaceId);

		if (!space) {
			throw storeIntegrityError(
				`deviceSpaceIndexMap references space "${spaceId}" which does not exist in spaceMap`
			);
		}

		const deviceIds = Array.from(this.spaceIdToMemberDeviceIds.get(spaceId) ?? []);

		return { space, deviceIds };
	}

	/**
	 * @param groupNodeId
	 * @returns Group object for the given groupNodeId
	 */
	public getGroup(groupNodeId: string): Group {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		return group;
	}

	/**
	 * @param spaceNodeId
	 * @returns Space object for the given spaceNodeId
	 */
	public getSpace(spaceNodeId: string): Space {
		const space = this.spaceIdToSpace.get(spaceNodeId);

		if (!space) {
			throw storeUsageError(
				`space "${spaceNodeId}" was not found in the store. Ensure the passed space nodeId is correct.`
			);
		}

		return space;
	}

	/**
	 * @param deviceNodeId
	 * @returns Member group nodeId for the given deviceNodeId
	 */
	public getDeviceMemberGroupId(deviceNodeId: string): string {
		const groupNodeId = this.deviceIdToGroupId.get(deviceNodeId);

		if (!groupNodeId) {
			throw storeUsageError(
				`device "${deviceNodeId}" was not found in any group. Ensure the passed device nodeId is correct.`
			);
		}

		return groupNodeId;
	}

	/**
	 * @returns Default name when adding a new group. UX wants this to be unique among all existing group names.
	 * Translated "New-Group-#" where # is the smallest positive integer that makes the name unique among all existing group names.
	 * Uses the same case-insensitive, trimmed comparison as group name validation.
	 */
	public getDefaultNameForNewGroup(): string {
		const existingGroupNames = new Set(
			Array.from(this.groupIdToGroup.values()).map((group) => group.name.trim().toLocaleLowerCase())
		);

		let number = 1;
		let defaultName = this.transloco.translate('cloud.device-management.organize.group-name-default', {
			number: number
		});
		while (existingGroupNames.has(defaultName.trim().toLocaleLowerCase())) {
			number++;
			defaultName = this.transloco.translate('cloud.device-management.organize.group-name-default', {
				number: number
			});
		}

		return defaultName;
	}

	/**
	 * @param groupNodeId
	 * @returns Whether this group's own root-to-group path reaches the maximum depth, counting the root as one.
	 */
	public isMaxGroupDepth(groupNodeId: string): boolean {
		this.groupStructureVersion();
		const idPath = this.groupIdToIdPath.get(groupNodeId);
		if (!idPath) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		return idPath.length >= MAX_GROUP_DEPTH;
	}

	/**
	 * @returns Whether the maximum number of groups has been reached for the organization
	 */
	public isMaxGroups(): boolean {
		this.groupStructureVersion();
		return this.groupIdToGroup.size >= MAX_GROUPS;
	}

	//
	// Mutation methods
	//
	public addGroup(group: Group): void {
		this.groupIdToGroup.set(group.id, group);

		if (group.parentId) {
			const parentChildren = this.groupIdToChildrenIds.get(group.parentId) ?? new Set<string>();
			parentChildren.add(group.id);
			this.groupIdToChildrenIds.set(group.parentId, parentChildren);
		}

		this.buildPathCache(group.id);
		this.groupStructureVersion.update((version) => version + 1);
	}

	public removeGroup(groupNodeId: string): void {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		if (this.groupIdToChildrenIds.get(groupNodeId)?.size) {
			throw storeUsageError(
				`group "${groupNodeId}" has child groups and cannot be removed. Remove child groups first.`
			);
		}

		if (this.groupIdToMemberDeviceIds.get(groupNodeId)?.size) {
			throw storeUsageError(
				`group "${groupNodeId}" has member devices and cannot be removed. Remove member devices first.`
			);
		}

		this.groupIdToMemberDeviceIds.delete(groupNodeId);

		// Remove cloud spaces belonging to this group
		for (const [spaceId, space] of Array.from(this.spaceIdToSpace)) {
			if (space.groupId === groupNodeId) {
				this.spaceIdToMemberDeviceIds.delete(spaceId);
				this.spaceIdToSpace.delete(spaceId);
			}
		}

		// Remove from parent's children index
		if (group.parentId) {
			this.groupIdToChildrenIds.get(group.parentId)?.delete(groupNodeId);
		}

		// Clean up own index entries
		this.groupIdToChildrenIds.delete(groupNodeId);
		this.groupIdToIdPath.delete(groupNodeId);
		this.groupIdToGroup.delete(groupNodeId);
		this.groupStructureVersion.update((version) => version + 1);
	}

	public moveGroup(groupNodeId: string, newParentNodeId: string | undefined): void {
		const group = this.groupIdToGroup.get(groupNodeId);
		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		// Remove from old parent's children index
		if (group.parentId) {
			this.groupIdToChildrenIds.get(group.parentId)?.delete(groupNodeId);
		}

		// If update moves the group to a new parent, add it to new parent's children index
		if (newParentNodeId) {
			const newParentChildren = this.groupIdToChildrenIds.get(newParentNodeId) ?? new Set<string>();
			newParentChildren.add(groupNodeId);
			this.groupIdToChildrenIds.set(newParentNodeId, newParentChildren);
		}

		// Update the group's parentId
		this.groupIdToGroup.set(groupNodeId, { ...group, parentId: newParentNodeId });

		// Rebuild path cache for this group and all descendants
		this.buildPathCache(groupNodeId);
		this.groupStructureVersion.update((version) => version + 1);
	}

	public renameGroup(groupNodeId: string, newName: string): void {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		this.groupIdToGroup.set(groupNodeId, { ...group, name: newName });
	}

	public setDeviceGroup(deviceNodeId: string, groupNodeId: string): void {
		const oldGroupId = this.deviceIdToGroupId.get(deviceNodeId);

		if (oldGroupId) {
			this.groupIdToMemberDeviceIds.get(oldGroupId)?.delete(deviceNodeId);
		}

		this.deviceIdToGroupId.set(deviceNodeId, groupNodeId);

		const memberDeviceIds = this.groupIdToMemberDeviceIds.get(groupNodeId) ?? new Set<string>();
		memberDeviceIds.add(deviceNodeId);
		this.groupIdToMemberDeviceIds.set(groupNodeId, memberDeviceIds);
	}

	public addOrUpdateSpace(space: Space): void {
		this.spaceIdToSpace.set(space.id, space);
	}

	public removeSpace(spaceNodeId: string): void {
		const space = this.spaceIdToSpace.get(spaceNodeId);

		if (!space) {
			throw storeUsageError(
				`space "${spaceNodeId}" was not found in the store. Ensure the passed space nodeId is correct.`
			);
		}

		// Remove device-to-space mappings, but keep devices in their group
		for (const deviceId of this.spaceIdToMemberDeviceIds.get(spaceNodeId) ?? []) {
			this.deviceIdToSpaceId.delete(deviceId);
		}

		this.spaceIdToMemberDeviceIds.delete(spaceNodeId);
		this.spaceIdToSpace.delete(spaceNodeId);
	}

	public setDeviceSpace(deviceNodeId: string, spaceNodeId: string): void {
		const oldSpaceId = this.deviceIdToSpaceId.get(deviceNodeId);

		if (oldSpaceId) {
			this.spaceIdToMemberDeviceIds.get(oldSpaceId)?.delete(deviceNodeId);
		}

		this.deviceIdToSpaceId.set(deviceNodeId, spaceNodeId);

		const memberDeviceIds = this.spaceIdToMemberDeviceIds.get(spaceNodeId) ?? new Set<string>();
		memberDeviceIds.add(deviceNodeId);
		this.spaceIdToMemberDeviceIds.set(spaceNodeId, memberDeviceIds);
	}

	public removeDevice(deviceNodeId: string): void {
		const groupId = this.deviceIdToGroupId.get(deviceNodeId);

		if (groupId) {
			this.groupIdToMemberDeviceIds.get(groupId)?.delete(deviceNodeId);
			this.deviceIdToGroupId.delete(deviceNodeId);
		}

		const spaceId = this.deviceIdToSpaceId.get(deviceNodeId);

		if (spaceId) {
			this.spaceIdToMemberDeviceIds.get(spaceId)?.delete(deviceNodeId);
			this.deviceIdToSpaceId.delete(deviceNodeId);
		}
	}

	public clear(): void {
		this.groupIdToGroup.clear();
		this.spaceIdToSpace.clear();
		this.groupIdToChildrenIds.clear();
		this.groupIdToMemberDeviceIds.clear();
		this.spaceIdToMemberDeviceIds.clear();
		this.deviceIdToGroupId.clear();
		this.deviceIdToSpaceId.clear();
		this.groupIdToIdPath.clear();
		this.groupStructureVersion.update((version) => version + 1);
	}

	//
	// Helpers
	//
	private buildPathCache(groupNodeId: string): void {
		const group = this.groupIdToGroup.get(groupNodeId);

		if (!group) {
			throw storeUsageError(
				`group "${groupNodeId}" was not found in the store. Ensure the passed group nodeId is correct.`
			);
		}

		const parentPath = group.parentId !== undefined ? (this.groupIdToIdPath.get(group.parentId) ?? []) : [];

		this.groupIdToIdPath.set(groupNodeId, [...parentPath, groupNodeId]);

		for (const childId of this.groupIdToChildrenIds.get(groupNodeId) ?? []) {
			this.buildPathCache(childId);
		}
	}

	private getTopLevelGroups(): Group[] {
		return Array.from(this.groupIdToGroup.values()).filter((group) => group.parentId === undefined);
	}
}
